import React, { useEffect, useState, useRef } from "react";
import Select from 'react-select';
import { Col, Row, Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";

import Attachments from "../../../components/Attachments"
import CustomEditor from "../../../components/Editor"
import { postRequestCallback } from "../../../helpers/Requests";

import { ucFirst } from '../../../helpers/generic';
import { colorOption, colorValue, colorValueSingle, imageOption } from '../../SelectComponents'
import { BsAsterisk } from "react-icons/bs";
import DatePicker from "../../../helpers/DatePicker";
import { linkify } from "../../../helpers/linkify";
import { earliestDueDate, MIN_DUE_DATE_DAYS_AHEAD } from "../../../helpers/dueDate";


const TicketModal = ({ auxiliary, reduxRefresh, closeModal }) => {
	const dispatch = useDispatch();
	const user = useSelector(state => state?.userReducer?.user);
	const isCustomer = user?.isCustomer || user?.is_customer;
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [dueDateError, setDueDateError] = useState('');

	const [formData, setFormData] = useState({
		title: '',
		status: null,
		type: null,
		assignees: [],
		department: null,
		priority: null,
		due_date: null,
		tags: [],
		description: '',
		attachments: [],
	});
	const [attachments, setAttachments] = useState([]);
	const description = useRef('');

	const [errors, setErrors] = useState({
		title: false,
		status: false,
		department: false,
		description: false,
	});

	const [options, setOptions] = useState({
		users: [],
		departments: [],
		priorities: [],
		statuses: [],
		types: [],
		tags: [],
	});

	const parseDate = (value) => {
		const date = new Date(value);
		const year = date.getFullYear();
		const month = ('0' + (date.getMonth() + 1)).slice(-2);
		const day = ('0' + date.getDate()).slice(-2);
		return { value: `${year}-${month}-${day}` };
	}

	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};
	const handleSelectChange = (name, value) => {
		if (name === 'department') {
			const autoAssignIds = (value?.auto_assign_user_ids || []).map(Number);
			const assignees = autoAssignIds.length
				? options.users.filter(user => autoAssignIds.includes(user.value))
				: [];
			setFormData({ ...formData, department: value, assignees });
			return;
		}
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const updatedFormData = {
			...formData,
			attachments: attachments.map(attachment => attachment.id),
			description: linkify(description.current.getHTML()),
		};
		setFormData(updatedFormData);

		const errors = {
			title: updatedFormData.title.length === 0,
			status: updatedFormData.status === null,
			department: updatedFormData.department === null,
			description: description.current.getText().length === 0,
		};
		setErrors(errors);

		const data = {
			...updatedFormData,
			status_id: updatedFormData.status?.value || null,
			department_id: updatedFormData.department?.value || null,
			priority_id: updatedFormData.priority?.value || null,
			type_id: updatedFormData.type?.value || null,
			assignees: (updatedFormData.assignees || []).map(user => user.value),
			tags: updatedFormData.tags?.map(tag => tag.value),
		}

		if (!errors.title && !errors.status && !errors.department && !errors.description) {
			setIsLoading(true);
			setErrorMessage('');
            // console.log('data debug::',data);
            let dueDateValue = data.due_date?.value ?? null;
			postRequestCallback('tickets', {
                ...data,
                due_date: dueDateValue
            }, () => {
				reduxRefresh();
				closeModal();
				setIsLoading(false);
			}, (error) => {
				setIsLoading(false);
				const fieldError = error?.response?.status === 422 && error.response.data?.errors?.due_date?.[0];
				if (fieldError) {
					setDueDateError(fieldError);
					return true;
				}
				setErrorMessage('There was an error creating the ticket. Please try again.');
			});
		} else {
			setErrorMessage('There are errors in the form.');
		}
	};

	useEffect(() => {
		const customerDepartmentIds = (user?.departments || []).map(d => d.id);
		const departmentList = isCustomer && customerDepartmentIds.length > 0
			? (auxiliary.departments || []).filter(d => customerDepartmentIds.includes(d.id))
			: (auxiliary.departments || []);
		setOptions({
			users: (auxiliary.users || []).map(u => ({ value: u.id, label: u.name, image: u.profile_image, description: u.email })),
			departments: departmentList.map(department => ({
				value: department.id,
				label: department.name,
				color: department.color,
				auto_assign_user_ids: department.auto_assign_user_ids || [],
			})),
			priorities: (auxiliary.priorities || []).map(priority => ({ value: priority.id, label: priority.name, color: priority.color })),
			// Overdue is set automatically based on due_date — a ticket can
			// never be created in that state, so it's never offered here.
			statuses: (auxiliary.statuses || [])
				.filter(status => status.name?.toLowerCase() !== 'overdue')
				.map(status => ({ value: status.id, label: ucFirst(status.name), color: status.color })),
			types: (auxiliary.types || []).map(type => ({ value: type.id, label: type.name, color: type.color })),
			tags: (auxiliary.tags || []).map(tag => ({ value: tag.id, label: tag.name, color: tag.color })),
		});
	}, [auxiliary, user?.departments, isCustomer]);

	const predefinedDates = [
		{
		  label: 'Today',
		  value: new Date()
		}, {
		  label: 'Tomorrow',
		  value: new Date(new Date().setDate(new Date().getDate() + 1))
		}, {
		  label: 'Next Week',
		  value: new Date(new Date().setDate(new Date().getDate() + 7))
		}
	]

	return (
		<Form onSubmit={handleSubmit}>
			<Modal show={true} onHide={closeModal} className="tickets-create-modal">
				<Modal.Header closeButton>
					<Modal.Title>New Ticket</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
					<Form.Group controlId="form.title">
						<Form.Label><b>Title</b><BsAsterisk className="required-field" /></Form.Label>
						<Form.Control type="text" value={formData.title} name="title" onChange={handleInputChange} placeholder="Enter title" required isInvalid={errors.title} />
					</Form.Group>
					<Form.Group controlId="form.description" className='mt-3'>
						<Form.Label><b>Description</b><BsAsterisk className="required-field" /></Form.Label>
						<CustomEditor placeholder="Please enter description" ref={description} name="description" isInvalid={errors.description} />
					</Form.Group>
					<Row>
						<Col sm={6}>
							<Form.Group controlId="form.status" className='mt-3'>
								<Form.Label><b>Status</b><BsAsterisk className="required-field" /></Form.Label>
								<Select options={options.statuses} components={{ Option: colorOption, SingleValue: colorValueSingle }} value={formData.status} onChange={(value) => handleSelectChange('status', value)} classNamePrefix="select-component" className={errors.status && "select-component-error"} />
							</Form.Group>
						</Col>
						<Col sm={6}>
							<Form.Group controlId="form.department" className='mt-3'>
								<Form.Label><b>Department</b><BsAsterisk className="required-field" /></Form.Label>
								<Select options={options.departments} components={{ Option: colorOption, SingleValue: colorValueSingle }} value={formData.department} onChange={(value) => handleSelectChange('department', value)} classNamePrefix="select-component" className={errors.department && "select-component-error"} />
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<Col sm={6}>
							<Form.Group controlId="form.assignee" className='mt-3'>
								<Form.Label><b>Assignee</b></Form.Label>
								<Select isMulti options={options.users} components={{ Option: imageOption }} value={formData.assignees} onChange={(value) => handleSelectChange('assignees', value ?? [])} classNamePrefix="select-component" isClearable={!isCustomer} closeMenuOnSelect={false} isDisabled={isCustomer} />
							</Form.Group>
						</Col>
						<Col sm={6}>
							<Form.Group controlId="form.due_date" className='mt-3'>
								<Form.Label><b>Due Date</b></Form.Label>
								<DatePicker
									handleDateChange={(value) => { setDueDateError(''); handleSelectChange('due_date', parseDate(value)); }}
									minDate={earliestDueDate()}
									invalid={!!dueDateError}
								/>
								{dueDateError ? (
									<Form.Text className="text-danger">{dueDateError}</Form.Text>
								) : (
									<Form.Text className="text-muted">
										Due date must be at least {MIN_DUE_DATE_DAYS_AHEAD} working days from today.
									</Form.Text>
								)}
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<Col sm={6}>
							<Form.Group controlId="form.type" className='mt-3'>
								<Form.Label><b>Type</b></Form.Label>
								<Select options={options.types} components={{ Option: colorOption, SingleValue: colorValueSingle }} value={formData.type} onChange={(value) => handleSelectChange('type', value)} classNamePrefix="select-component" isClearable={true} />
							</Form.Group>
						</Col>
						<Col sm={6}>
							<Form.Group controlId="form.priority" className='mt-3'>
								<Form.Label><b>Priority</b></Form.Label>
								<Select options={options.priorities} components={{ Option: colorOption, SingleValue: colorValueSingle }} value={formData.priority} onChange={(value) => handleSelectChange('priority', value)} classNamePrefix="select-component" isClearable={true} />
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<Col sm={6}>
							<Form.Group controlId="form.tags" className='mt-3'>
								<Form.Label><b>Tags</b></Form.Label>
								<Select options={options.tags} components={{ Option: colorOption, MultiValueLabel: colorValue }} value={formData.tags} onChange={(value) => handleSelectChange('tags', value)} classNamePrefix="select-component" isMulti={true} isClearable={true} />
							</Form.Group>
						</Col>
						<Col sm={6}>
							<Form.Group controlId="form.attachments" className='mt-3'>
								<Form.Label><b>Attachments</b></Form.Label>
								<Attachments attachments={attachments} setAttachments={setAttachments} showAll={false} />
							</Form.Group>
						</Col>
					</Row>
				</Modal.Body >
				<Modal.Footer>
					<Button variant="secondary" onClick={closeModal} disabled={isLoading}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={isLoading} onClick={handleSubmit}>
						{isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save'}
					</Button>
				</Modal.Footer>
			</Modal >
		</Form>
	);
}

export default TicketModal
